import { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";

interface Props {
  open: boolean;
  onClose: () => void;
  onDetected: (code: string) => void;
}

const SCANNER_ELEMENT_ID = "im-qr-scanner-region";

/**
 * Modal con escaner html5-qrcode. Preferencia por camara trasera
 * (facingMode: environment). Fallback a input manual.
 */
export function QRScanner({ open, onClose, onDetected }: Props) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [manualMode, setManualMode] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  // Stop scanner helper
  async function stopScanner() {
    const s = scannerRef.current;
    if (!s) return;
    try {
      if ((s as unknown as { isScanning?: boolean }).isScanning) {
        await s.stop();
      }
      await s.clear();
    } catch {
      // ignore
    }
    scannerRef.current = null;
  }

  useEffect(() => {
    if (!open || manualMode) return;

    let cancelled = false;
    setError(null);
    setStarting(true);

    const start = async () => {
      try {
        const scanner = new Html5Qrcode(SCANNER_ELEMENT_ID, {
          formatsToSupport: [
            Html5QrcodeSupportedFormats.QR_CODE,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.CODE_39,
          ],
          verbose: false,
        });
        scannerRef.current = scanner;

        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decodedText) => {
            if (cancelled) return;
            onDetected(decodedText);
          },
          () => {
            // frame sin QR: silencio
          },
        );
      } catch (e) {
        if (cancelled) return;
        setError(
          "No se pudo acceder a la cámara. Usa el modo manual o revisa permisos.",
        );
      } finally {
        if (!cancelled) setStarting(false);
      }
    };

    void start();

    return () => {
      cancelled = true;
      void stopScanner();
    };
  }, [open, manualMode, onDetected]);

  // Cleanup al cerrar
  useEffect(() => {
    if (!open) void stopScanner();
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="w-full max-w-md rounded-xl bg-slate-900 p-4 ring-1 ring-slate-700">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-100">
            {manualMode ? "Ingresar código QR" : "Escanear QR"}
          </h3>
          <button
            onClick={onClose}
            className="rounded px-2 py-1 text-xs text-slate-400 hover:bg-slate-800"
            aria-label="Cerrar"
          >
            Cerrar
          </button>
        </div>

        {!manualMode && (
          <>
            <div
              id={SCANNER_ELEMENT_ID}
              className="aspect-square w-full overflow-hidden rounded-md bg-black"
            />
            {starting && (
              <p className="mt-2 text-xs text-slate-400">Iniciando cámara...</p>
            )}
            {error && (
              <p className="mt-2 text-xs text-red-400" role="alert">
                {error}
              </p>
            )}
            <button
              onClick={() => {
                void stopScanner();
                setManualMode(true);
              }}
              className="mt-3 w-full rounded-md border border-slate-700 px-3 py-2 text-xs text-slate-300 hover:bg-slate-800"
            >
              Ingresar código manualmente
            </button>
          </>
        )}

        {manualMode && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const v = manualCode.trim();
              if (v) onDetected(v);
            }}
            className="flex flex-col gap-3"
          >
            <input
              type="text"
              inputMode="text"
              autoFocus
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              maxLength={500}
              className="rounded-md bg-slate-800 px-3 py-2 text-sm text-slate-100 ring-1 ring-slate-700 focus:outline-none focus:ring-brand-light"
              placeholder="Código QR"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setManualMode(false)}
                className="flex-1 rounded-md border border-slate-700 px-3 py-2 text-xs text-slate-300 hover:bg-slate-800"
              >
                Volver a cámara
              </button>
              <button
                type="submit"
                disabled={!manualCode.trim()}
                className="flex-1 rounded-md bg-brand-light px-3 py-2 text-xs font-semibold text-white hover:bg-brand disabled:opacity-50"
              >
                Confirmar
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
